import { FilePlusIcon } from '@radix-ui/react-icons'
import { message } from 'antd'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { useGeckoAnalytics } from 'features/analytics'
import { useKestrel } from 'features/kestrelViewer'
import { ViewToggle } from 'features/kestrelViewer/ui/tools/ViewToggle'
import { convertAnnotationsToPDFWithScreenshots } from 'features/kestrelViewer/utils/pdfExportWithScreenshots'
import React, { useCallback, useState } from 'react'
import { KVAnnotation } from 'types'

// Simple CSS spinner component
const Spinner: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <div
    style={{
      width: size,
      height: size,
      border: '2px solid #f3f3f3',
      borderTop: '2px solid #333',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    }}
  />
)

export const DownloadDefectsReportWithScreenshots: React.FC = () => {
  const { trackButtonClicked } = useGeckoAnalytics()
  const { annotations, primitives, measurements, kestrelModel } = useKestrel()
  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState<{
    current: number
    total: number
    step: string
  } | null>(null)

  const captureAnnotationScreenshot = useCallback(
    async (
      annotationId: string,
      annotation: KVAnnotation,
      position?: { x: number; y: number; z: number }
    ) => {
      if (!kestrelModel?.id) {
        throw new Error('No model loaded')
      }

      // Check if screenshot bridge is available
      if (!window.kestrelScreenshotBridge) {
        console.error('❌ Screenshot bridge not available')
        throw new Error('Screenshot bridge not available')
      }

      const timestamp = Date.now()
      const rolodexPath = `screenshots/${annotationId}/defect_${timestamp}.png`

      // For polygon annotations, we need to handle them differently
      const isPolygon = annotation.geometry?.kind === 'polygon'
      const annotationIds = isPolygon ? [] : [annotationId]
      const surfaceAreaIds = isPolygon ? [annotationId] : []

      // Get the normal vector for camera positioning
      const normal = annotation.centeredNormal || annotation.geometry?.normal

      // Calculate camera position and rotation
      let cameraPosition: { x: number; y: number; z: number }
      let cameraRotation = 'P=0 Y=0 R=0' // Default

      if (normal && position) {
        // Position camera along the normal vector at a good distance (30 units)
        const distance = 30
        cameraPosition = {
          x: position.x + normal.x * distance,
          y: position.y + normal.y * distance,
          z: position.z + normal.z * distance,
        }

        // Calculate pitch and yaw to look at the annotation
        // Pitch: angle from horizontal plane
        const horizontalDist = Math.sqrt(
          normal.x * normal.x + normal.y * normal.y
        )
        const pitch = -Math.atan2(normal.z, horizontalDist) * (180 / Math.PI)

        // Yaw: angle in horizontal plane
        const yaw = Math.atan2(normal.y, normal.x) * (180 / Math.PI)

        cameraRotation = `P=${pitch.toFixed(2)} Y=${yaw.toFixed(2)} R=0`
      } else if (position) {
        // No normal available, use position with default offset
        cameraPosition = {
          x: position.x,
          y: position.y,
          z: position.z + 30, // Offset above
        }
      } else {
        // No position available (shouldn't happen for valid annotations)
        cameraPosition = { x: 0, y: 0, z: 30 }
      }

      // Format position and rotation as strings (required by screenshot bridge)
      const positionStr = `X=${cameraPosition.x} Y=${cameraPosition.y} Z=${cameraPosition.z}`
      const rotationStr = cameraRotation

      const result = await window.kestrelScreenshotBridge.captureScreenshot({
        position: positionStr,
        rotation: rotationStr,
        width: 1920,
        height: 1080,
        fov: 60,
        annotationIds,
        surfaceAreaIds,
        showAnnotationMarkers: true, // Always show annotations in screenshots
        entryId: kestrelModel.id,
        rolodexPath,
        uploadToRolodex: false, // Don't need to save to rolodex, just get the base64
      })

      if (!result.success) {
        throw new Error(result.error || 'Screenshot capture failed')
      }

      // Add data URL prefix if not already present
      let imageBase64 = result.imageBase64
      if (imageBase64 && !imageBase64.startsWith('data:')) {
        imageBase64 = `data:image/png;base64,${imageBase64}`
      }

      return {
        imageBase64,
        path: rolodexPath,
      }
    },
    [kestrelModel]
  )

  const handleDownload = async () => {
    if (isGenerating) return // Prevent multiple clicks

    const totalCount =
      (annotations?.length ?? 0) +
      (primitives?.length ?? 0) +
      (measurements?.length ?? 0)

    if (totalCount === 0) {
      message.warning('No annotations, primitives, or measurements to export')
      return
    }

    if (!kestrelModel?.id) {
      message.error('No model loaded')
      return
    }

    setIsGenerating(true)
    setProgress({ current: 0, total: totalCount, step: 'Starting...' })

    try {
      const visibleAnnotations = annotations?.filter((a) => a.visible) ?? []
      const visiblePrimitives = primitives?.filter((p) => p.visible) ?? []
      const visibleMeasurements = measurements?.filter((m) => m.visible) ?? []

      const visibleCount =
        visibleAnnotations.length +
        visiblePrimitives.length +
        visibleMeasurements.length

      if (visibleCount === 0) {
        message.warning('No visible items to export')
        return
      }

      setProgress({
        current: 0,
        total: visibleCount,
        step: 'Capturing screenshots...',
      })

      // Capture screenshots for each item
      const screenshotData: Record<
        string,
        { imageBase64?: string; path: string }
      > = {}

      let captureCount = 0

      // Capture annotation screenshots
      for (const annotation of visibleAnnotations) {
        captureCount++
        setProgress({
          current: captureCount,
          total: visibleCount,
          step: `Capturing screenshot ${captureCount} of ${visibleCount}...`,
        })

        try {
          // Get position - use centeredPosition if available, otherwise geometry position
          let position = annotation.centeredPosition

          if (!position && annotation.geometry?.kind === 'point') {
            position = annotation.geometry.position
          } else if (!position && annotation.geometry?.kind === 'polygon') {
            // Calculate centroid from polygon points as fallback
            const points = annotation.geometry.points
            if (points && points.length > 0) {
              const sum = points.reduce(
                (acc, p) => ({
                  x: acc.x + p.x,
                  y: acc.y + p.y,
                  z: acc.z + p.z,
                }),
                { x: 0, y: 0, z: 0 }
              )
              position = {
                x: sum.x / points.length,
                y: sum.y / points.length,
                z: sum.z / points.length,
              }
            }
          }

          const result = await captureAnnotationScreenshot(
            annotation.id,
            annotation,
            position
          )

          screenshotData[annotation.id] = result
        } catch {
          // Continue with other screenshots even if one fails
          screenshotData[annotation.id] = { path: '' }
        }
      }

      // Capture primitive screenshots
      for (const primitive of visiblePrimitives) {
        captureCount++
        setProgress({
          current: captureCount,
          total: visibleCount,
          step: `Capturing screenshot ${captureCount} of ${visibleCount}...`,
        })

        try {
          // Calculate position from primitive's transform location
          const position = primitive.transform?.location

          if (!position) {
            console.warn(
              `[Screenshot] No position found for primitive ${primitive.id}`,
              { transform: primitive.transform, primitive }
            )
            screenshotData[primitive.id] = { path: '' }
            continue
          }

          // Create a minimal annotation-like object for screenshot capture
          const primitiveAsAnnotation = {
            id: primitive.id,
            geometry: { kind: 'point' as const },
            centeredPosition: position,
          } as KVAnnotation

          const result = await captureAnnotationScreenshot(
            primitive.id,
            primitiveAsAnnotation,
            position
          )

          screenshotData[primitive.id] = result
        } catch (error) {
          console.error(
            `[Screenshot] Failed to capture primitive ${primitive.id}:`,
            error
          )
          screenshotData[primitive.id] = { path: '' }
        }
      }

      // Capture measurement screenshots
      for (const measurement of visibleMeasurements) {
        captureCount++
        setProgress({
          current: captureCount,
          total: visibleCount,
          step: `Capturing screenshot ${captureCount} of ${visibleCount}...`,
        })

        try {
          // Calculate centroid from all measurement segment points
          let position: { x: number; y: number; z: number } | undefined

          if (measurement.segments && measurement.segments.length > 0) {
            const allPoints: Array<{ x: number; y: number; z: number }> = []

            // Collect all start and end points from segments
            for (const segment of measurement.segments) {
              try {
                // Parse start position (format: "X=... Y=... Z=...")
                if (segment.measurementSegmentStartPosition) {
                  const startMatch =
                    segment.measurementSegmentStartPosition.match(
                      /X=([0-9.-]+)\s+Y=([0-9.-]+)\s+Z=([0-9.-]+)/
                    )
                  if (startMatch) {
                    allPoints.push({
                      x: parseFloat(startMatch[1]),
                      y: parseFloat(startMatch[2]),
                      z: parseFloat(startMatch[3]),
                    })
                  }
                }

                // Parse end position
                if (segment.measurementSegmentEndPosition) {
                  const endMatch = segment.measurementSegmentEndPosition.match(
                    /X=([0-9.-]+)\s+Y=([0-9.-]+)\s+Z=([0-9.-]+)/
                  )
                  if (endMatch) {
                    allPoints.push({
                      x: parseFloat(endMatch[1]),
                      y: parseFloat(endMatch[2]),
                      z: parseFloat(endMatch[3]),
                    })
                  }
                }
              } catch {
                // Skip invalid segments
              }
            }

            // Calculate centroid
            if (allPoints.length > 0) {
              const sum = allPoints.reduce(
                (acc, p) => ({
                  x: acc.x + p.x,
                  y: acc.y + p.y,
                  z: acc.z + p.z,
                }),
                { x: 0, y: 0, z: 0 }
              )
              position = {
                x: sum.x / allPoints.length,
                y: sum.y / allPoints.length,
                z: sum.z / allPoints.length,
              }
            }
          }

          if (!position) {
            screenshotData[measurement.id] = { path: '' }
            continue
          }

          // Create a minimal annotation-like object for screenshot capture
          const measurementAsAnnotation = {
            id: measurement.id,
            geometry: { kind: 'point' as const },
            centeredPosition: position,
          } as KVAnnotation

          const result = await captureAnnotationScreenshot(
            measurement.id,
            measurementAsAnnotation,
            position
          )

          screenshotData[measurement.id] = result
        } catch {
          screenshotData[measurement.id] = { path: '' }
        }
      }

      setProgress({
        current: visibleCount,
        total: visibleCount,
        step: 'Generating PDF...',
      })

      // Extract component metadata from kestrelModel tags
      const metadata = {
        organizationName: kestrelModel?.tags.organization,
        siteName: kestrelModel?.tags.site,
        componentName: kestrelModel?.tags.component,
      }

      // Convert annotations, primitives, and measurements to PDF with screenshots
      const pdfBlob = await convertAnnotationsToPDFWithScreenshots(
        visibleAnnotations,
        visiblePrimitives,
        visibleMeasurements,
        screenshotData,
        metadata
      )

      const url = URL.createObjectURL(pdfBlob)

      const link = document.createElement('a')
      link.href = url
      link.download = 'annotations-report-with-screenshots.pdf'
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      const itemsText = [
        visibleAnnotations.length > 0
          ? `${visibleAnnotations.length} annotation${visibleAnnotations.length !== 1 ? 's' : ''}`
          : null,
        visiblePrimitives.length > 0
          ? `${visiblePrimitives.length} primitive${visiblePrimitives.length !== 1 ? 's' : ''}`
          : null,
        visibleMeasurements.length > 0
          ? `${visibleMeasurements.length} measurement${visibleMeasurements.length !== 1 ? 's' : ''}`
          : null,
      ]
        .filter(Boolean)
        .join(', ')

      message.success(`Successfully generated report with ${itemsText}`)

      trackButtonClicked({
        button_name: 'kestrel_download_pdf_with_screenshots',
        kestrel_model_id: kestrelModel?.data.kestrel_model_id,
      })
    } catch {
      message.error('Failed to generate PDF report. Please try again.')
    } finally {
      setIsGenerating(false)
      setProgress(null)
    }
  }

  return (
    <TooltipWrapper
      side={'top'}
      offset={5}
      label={
        isGenerating
          ? progress
            ? `${progress.step} (${progress.current}/${progress.total})`
            : 'Generating PDF...'
          : 'Download Annotation Report with Screenshots'
      }>
      <ViewToggle
        onClick={() => {
          if (!isGenerating) {
            handleDownload()
          }
        }}>
        {isGenerating ? (
          <Spinner size={22} />
        ) : (
          <FilePlusIcon width={22} height={22} />
        )}
      </ViewToggle>
    </TooltipWrapper>
  )
}
